import { useEffect } from 'react';
import { projectMissionEvidence, type MissionEvidenceProjection } from '../../lib/missions/evidence';
import { createMissionSpineRunId, recordMissionSpineMarker } from '../../lib/mission-spine/instrumentation';

interface MissionEvidencePanelProps {
  mission?: Record<string, unknown> | null;
  projection?: MissionEvidenceProjection;
  title?: string;
}

const KIND_LABELS: Record<string, string> = {
  reason: 'Reason',
  composition: 'Composition',
  signal: 'Signal',
  data_source: 'Source',
  evidence_ref: 'Reference',
  entity: 'Entity',
};

export function MissionEvidencePanel({
  mission,
  projection,
  title = 'Why We Recommend This',
}: MissionEvidencePanelProps) {
  const evidence = projection ?? projectMissionEvidence(mission);

  useEffect(() => {
    recordMissionSpineMarker({
      runId: createMissionSpineRunId('slice-02-evidence'),
      hop: 'evidence_render',
      status: evidence.empty ? 'skipped' : 'success',
      reasonCode: evidence.empty ? 'empty_evidence' : 'body_safe_evidence_rendered',
      counts: {
        rowCount: evidence.rows.length,
      },
    }, { log: false });
  }, [evidence.empty, evidence.rows.length]);

  return (
    <section data-testid="mission-evidence-panel" className="bg-slate-800 rounded-xl border border-slate-700 p-5 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-100">{title}</h2>
        <span data-testid="mission-evidence-count" className="text-xs text-slate-500">
          {evidence.rows.length} rows
        </span>
      </div>

      {evidence.empty ? (
        <div data-testid="mission-evidence-empty" className="rounded-lg bg-slate-700/40 p-3">
          <p className="text-sm text-slate-400">No body-safe evidence is available for this mission yet.</p>
        </div>
      ) : (
        <dl data-testid="mission-evidence-rows" className="space-y-2">
          {evidence.rows.map((row, index) => (
            <div
              key={`${row.kind}-${row.label}-${index}`}
              data-testid="mission-evidence-row"
              data-kind={row.kind}
              className="rounded-lg bg-slate-700/45 px-3 py-2"
            >
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                {KIND_LABELS[row.kind] ?? row.kind} · {row.label}
              </dt>
              <dd className="mt-1 text-sm text-slate-200 break-words">{row.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}
